import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Settings, Loader2, PalmtreeIcon, Wallet, Bell, Save, Store, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { MKT_GOLD } from "../../lib/marketplace";
import { apiGetConfiguracion, apiGuardarConfiguracion } from "../../lib/marketplaceApi";
import { humanizeAxiosError } from "../../lib/api";

const METODOS = [
  { id: "transferencia", label: "Transferencia bancaria" },
  { id: "yape", label: "Yape" },
  { id: "plin", label: "Plin" },
];
const NOTIS = [
  { key: "ventas", label: "Nuevas ventas", desc: "Cuando un comprador adquiere un producto o curso tuyo." },
  { key: "reembolsos", label: "Reembolsos", desc: "Cuando se procesa un reembolso que afecta tu saldo." },
  { key: "retiros", label: "Retiros", desc: "Actualizaciones de tus solicitudes de retiro." },
  { key: "moderacion", label: "Moderación", desc: "Resultados de revisión de tu tienda, productos y cursos." },
];

function Toggle({ checked, onChange, testid }) {
  return (
    <button type="button" onClick={() => onChange(!checked)} data-testid={testid}
      className="relative inline-flex h-6 w-11 items-center rounded-full transition-colors flex-shrink-0"
      style={{ backgroundColor: checked ? MKT_GOLD : "#CBD5E1" }}>
      <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${checked ? "translate-x-6" : "translate-x-1"}`} />
    </button>
  );
}

export default function ConfiguracionVendedor() {
  const [cfg, setCfg] = useState(undefined);
  const [guardando, setGuardando] = useState(false);
  const [sinTienda, setSinTienda] = useState(false);

  useEffect(() => {
    apiGetConfiguracion()
      .then((d) => setCfg({
        vacaciones: !!d.vacaciones,
        payout_metodo: d.payout_metodo || "transferencia",
        payout_datos: d.payout_datos || "",
        notif_prefs: { ventas: true, reembolsos: true, retiros: true, moderacion: true, ...(d.notif_prefs || {}) },
        estado: d.estado, nombre: d.nombre,
      }))
      .catch((e) => { if (e?.response?.status === 404) setSinTienda(true); setCfg(null); });
  }, []);

  const guardar = async () => {
    setGuardando(true);
    try {
      const r = await apiGuardarConfiguracion({
        vacaciones: cfg.vacaciones,
        payout_metodo: cfg.payout_metodo,
        payout_datos: cfg.payout_datos,
        notif_prefs: cfg.notif_prefs,
      });
      setCfg((c) => ({ ...c, ...r }));
      toast.success("Configuración guardada.");
    } catch (e) { toast.error(humanizeAxiosError(e)); }
    finally { setGuardando(false); }
  };

  if (cfg === undefined) return <div className="flex items-center gap-2 text-slate-500 py-16"><Loader2 className="animate-spin" size={16} /> Cargando configuración…</div>;

  if (sinTienda) {
    return (
      <div className="text-center py-16" data-testid="config-sin-tienda">
        <Store size={40} className="mx-auto text-slate-300" />
        <p className="mt-3 text-slate-500">Primero crea tu tienda para configurar tus preferencias.</p>
        <Link to="/marketplace/crear-tienda" className="mt-4 inline-block font-bold" style={{ color: MKT_GOLD }}>Crear mi tienda</Link>
      </div>
    );
  }
  if (!cfg) return <p className="text-sm text-slate-400 py-10">No se pudo cargar la configuración.</p>;

  const setNoti = (k, v) => setCfg((c) => ({ ...c, notif_prefs: { ...c.notif_prefs, [k]: v } }));

  return (
    <div className="max-w-3xl space-y-6" data-testid="config-vendedor">
      <div>
        <h1 className="text-2xl font-extrabold text-slate-900 inline-flex items-center gap-2"><Settings size={24} style={{ color: MKT_GOLD }} /> Configuración</h1>
        <p className="mt-1 text-sm text-slate-500">Preferencias de tu tienda «{cfg.nombre}».</p>
      </div>

      {/* 1. Modo vacaciones */}
      <section className="bg-white rounded-2xl border border-slate-200 p-5" data-testid="config-vacaciones-card">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 className="font-bold text-slate-900 inline-flex items-center gap-2"><PalmtreeIcon size={18} style={{ color: MKT_GOLD }} /> Pausar tienda temporalmente</h2>
            <p className="mt-1 text-sm text-slate-500">Mientras esté activa la pausa, tus productos y cursos no aparecen en el catálogo público ni se pueden comprar. Los compradores actuales conservan su acceso.</p>
          </div>
          <Toggle checked={cfg.vacaciones} onChange={(v) => setCfg((c) => ({ ...c, vacaciones: v }))} testid="config-toggle-vacaciones" />
        </div>
        {cfg.vacaciones && (
          <p className="mt-3 text-xs font-semibold text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 inline-flex items-center gap-1.5" data-testid="config-vacaciones-aviso">
            <AlertTriangle size={13} /> Tu tienda está en pausa: no es visible para nuevos compradores.
          </p>
        )}
      </section>

      {/* 2. Datos de retiro */}
      <section className="bg-white rounded-2xl border border-slate-200 p-5" data-testid="config-payout-card">
        <h2 className="font-bold text-slate-900 inline-flex items-center gap-2"><Wallet size={18} style={{ color: MKT_GOLD }} /> Datos de retiro por defecto</h2>
        <p className="mt-1 text-sm text-slate-500">Se usan para prellenar tu solicitud de retiro en Comisiones.</p>
        <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-1">Método preferido</label>
            <select value={cfg.payout_metodo} onChange={(e) => setCfg((c) => ({ ...c, payout_metodo: e.target.value }))} data-testid="config-payout-metodo"
              className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-200">
              {METODOS.map((m) => <option key={m.id} value={m.id}>{m.label}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-1">Datos ({cfg.payout_metodo === "transferencia" ? "N° de cuenta / CCI" : "N° de celular"})</label>
            <input value={cfg.payout_datos} onChange={(e) => setCfg((c) => ({ ...c, payout_datos: e.target.value }))} maxLength={500} data-testid="config-payout-datos"
              className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-200" placeholder="Ej. 002-1234567890-01" />
          </div>
        </div>
      </section>

      {/* 3. Notificaciones */}
      <section className="bg-white rounded-2xl border border-slate-200 p-5" data-testid="config-notif-card">
        <h2 className="font-bold text-slate-900 inline-flex items-center gap-2"><Bell size={18} style={{ color: MKT_GOLD }} /> Preferencias de notificaciones</h2>
        <div className="mt-4 divide-y divide-slate-100">
          {NOTIS.map((n) => (
            <div key={n.key} className="flex items-center justify-between gap-4 py-3">
              <div>
                <p className="text-sm font-semibold text-slate-800">{n.label}</p>
                <p className="text-xs text-slate-500">{n.desc}</p>
              </div>
              <Toggle checked={!!cfg.notif_prefs[n.key]} onChange={(v) => setNoti(n.key, v)} testid={`config-notif-${n.key}`} />
            </div>
          ))}
        </div>
      </section>

      <div className="flex justify-end">
        <button onClick={guardar} disabled={guardando} data-testid="config-guardar"
          className="inline-flex items-center gap-2 px-6 py-3 rounded-xl text-white font-bold hover:brightness-110 disabled:opacity-60" style={{ backgroundColor: MKT_GOLD }}>
          {guardando ? <Loader2 size={18} className="animate-spin" /> : <Save size={18} />} Guardar cambios
        </button>
      </div>
    </div>
  );
}
